import React from 'react';
import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Target,
  AlertTriangle,
  TrendingUp,
  Award,
  Smartphone,
  Wrench,
  Info,
  CheckCircle,
  FileText
} from 'lucide-react';

export const CampaignsGuidePage: React.FC = () => {
  return (
    <DocsLayout
      title='Campaign Management'
      description='Configure and prioritize recall campaigns'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Features', path: '/docs/features' },
        { label: 'Campaign Management' }
      ]}
    >
      <div className='max-w-[1000px]'>
        {/* What is a Campaign */}
        <div className='mb-20'>
          <div className='bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-600 p-6 rounded-r-xl'>
            <div className='flex gap-4'>
              <Info className='h-6 w-6 text-blue-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-base font-semibold text-blue-900 dark:text-blue-100 mb-2'>
                  What is a Campaign?
                </h4>
                <p className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                  A campaign is a manufacturer's recall notice (like "24V123" or "N242354370"). When
                  a car manufacturer discovers a safety issue or defect, they create a campaign and
                  assign it a number. In Vehix, campaigns group together all the vehicles affected
                  by that specific recall, making it easier to manage and complete the work.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Why Campaign Management Matters */}
        <section className='mb-20' id='why-campaign-management-matters'>
          <h2 className='text-3xl font-bold mb-6'>Why Campaign Management Matters</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Not all recalls are equally urgent. Some involve safety-critical issues that need
            immediate attention, while others are minor and can wait. Campaign Management lets you
            tell Vehix which recalls should be prioritized and which technicians are qualified to do
            the work.
          </p>

          <div className='grid gap-6 sm:grid-cols-2'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-3'>
                  <Target className='h-6 w-6 text-primary' />
                  <CardTitle className='text-lg font-semibold'>Focus on What Matters</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Set priorities so your team tackles the most important recalls first.
                  Safety-critical campaigns get done before minor issues.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-3'>
                  <Award className='h-6 w-6 text-primary' />
                  <CardTitle className='text-lg font-semibold'>Match Work to Skills</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Assign skill level requirements so complex repairs go to experienced technicians
                  and simple ones can be handled by anyone.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-3'>
                  <Smartphone className='h-6 w-6 text-primary' />
                  <CardTitle className='text-lg font-semibold'>Enable Mobile Service</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Mark which campaigns can be completed at the customer's home or workplace, making
                  service more convenient.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-3'>
                  <TrendingUp className='h-6 w-6 text-primary' />
                  <CardTitle className='text-lg font-semibold'>Track Progress</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  See how many recalls are complete for each campaign and identify which ones need
                  more attention.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* How to Access Campaign Management */}
        <section className='mb-20' id='how-to-access-campaign-management'>
          <div className='flex items-center gap-3 mb-6'>
            <Wrench className='h-8 w-8 text-primary' />
            <h2 className='text-3xl font-bold'>How to Access Campaign Management</h2>
          </div>

          <div className='space-y-6 mb-10'>
            <div className='relative flex gap-4'>
              <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                1
              </div>
              <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
              <div className='flex-1 pb-2'>
                <h4 className='text-lg font-semibold text-foreground mb-2'>Open the Sidebar</h4>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Look at the left side of your screen for the main navigation menu.
                </p>
              </div>
            </div>

            <div className='relative flex gap-4'>
              <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                2
              </div>
              <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
              <div className='flex-1 pb-2'>
                <h4 className='text-lg font-semibold text-foreground mb-2'>
                  Find the Configuration Section
                </h4>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Scroll down until you see the "Configuration" heading. This is where system
                  settings live.
                </p>
              </div>
            </div>

            <div className='relative flex gap-4'>
              <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                3
              </div>
              <div className='flex-1 pb-2'>
                <h4 className='text-lg font-semibold text-foreground mb-2'>Click Campaigns</h4>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Under Configuration, click "Campaigns". You will see a list of all active recall
                  campaigns.
                </p>
              </div>
            </div>
          </div>

          <div className='bg-amber-50 dark:bg-amber-950/30 border-l-4 border-amber-600 p-6 rounded-r-xl'>
            <div className='flex gap-4'>
              <AlertTriangle className='h-6 w-6 text-amber-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-base font-semibold text-amber-900 dark:text-amber-100 mb-2'>
                  Who Can Access This?
                </h4>
                <p className='text-sm leading-6 text-amber-800 dark:text-amber-200'>
                  Campaign Management is typically restricted to Service Managers, Field Operations
                  Directors, and Dealership Admins. If you don't see this option, you may not have
                  permission. Contact your supervisor if you think you need access.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Understanding the Campaign List */}
        <section className='mb-20' id='understanding-the-campaign-list'>
          <h2 className='text-3xl font-bold mb-6'>Understanding the Campaign List</h2>
          <p className='text-sm text-muted-foreground mb-6 leading-6'>
            When you open Campaign Management, you'll see a table with columns showing:
          </p>
          <ul className='space-y-3 mb-10 text-sm leading-6'>
            <li>
              <strong>Campaign Number:</strong> The manufacturer's unique ID (like "24V123" or
              "N242354370")
            </li>
            <li>
              <strong>Description:</strong> What the recall is about in plain language
            </li>
            <li>
              <strong>Total Recalls:</strong> How many vehicles are affected by this campaign
            </li>
            <li>
              <strong>Completed:</strong> How many have already been serviced
            </li>
            <li>
              <strong>Priority:</strong> High, Medium, or Low (you can change this)
            </li>
            <li>
              <strong>Skill Level:</strong> What kind of technician is needed (you can change this)
            </li>
            <li>
              <strong>Mobile Eligible:</strong> Whether it can be done at customer location (you can
              change this)
            </li>
          </ul>

          <div className='bg-muted/30 border-l-4 border-border p-6 rounded-r-xl'>
            <div className='flex gap-4'>
              <FileText className='h-6 w-6 text-muted-foreground flex-shrink-0 mt-1' />
              <div>
                <p className='text-sm leading-6'>
                  <strong>Tip:</strong> Click the column headers to sort by that column. For
                  example, click "Total Recalls" to see which campaigns affect the most vehicles, or
                  click "Completed" to see which ones are falling behind.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Setting Campaign Priority */}
        <section className='mb-20' id='setting-campaign-priority'>
          <h2 className='text-3xl font-bold mb-6'>Setting Campaign Priority</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Priority tells your team (and Vehix's routing system) which recalls should be completed
            first. Here's how to think about priority levels:
          </p>

          <div className='space-y-6 mb-10'>
            <div className='flex items-start gap-3 p-6 bg-red-50 dark:bg-red-950 rounded-lg border-l-4 border-red-500'>
              <Badge className='bg-red-600 text-white mt-1 flex-shrink-0'>High Priority</Badge>
              <div className='flex-1'>
                <p className='font-semibold mb-1 text-red-900 dark:text-red-100 text-sm'>
                  Use For:
                </p>
                <ul className='text-sm text-red-800 dark:text-red-200 space-y-1 list-disc list-inside leading-6'>
                  <li>Safety-critical recalls (brakes, airbags, steering)</li>
                  <li>Recalls with manufacturer deadlines approaching</li>
                  <li>Stop-sale or do-not-drive campaigns</li>
                  <li>High-profile recalls generating customer complaints</li>
                </ul>
                <p className='text-xs italic mt-2 text-red-700 dark:text-red-300 leading-5'>
                  <strong>What this means:</strong> These get done first, every time. Vehix will
                  suggest these for routing before anything else.
                </p>
              </div>
            </div>

            <div className='flex items-start gap-3 p-6 bg-yellow-50 dark:bg-yellow-950 rounded-lg border-l-4 border-yellow-500'>
              <Badge className='bg-yellow-600 text-white mt-1 flex-shrink-0'>Medium Priority</Badge>
              <div className='flex-1'>
                <p className='font-semibold mb-1 text-yellow-900 dark:text-yellow-100 text-sm'>
                  Use For:
                </p>
                <ul className='text-sm text-yellow-800 dark:text-yellow-200 space-y-1 list-disc list-inside leading-6'>
                  <li>Standard recalls with normal timelines</li>
                  <li>Most manufacturer campaigns</li>
                  <li>Recalls that should be done but aren't urgent</li>
                </ul>
                <p className='text-xs italic mt-2 text-yellow-700 dark:text-yellow-300 leading-5'>
                  <strong>What this means:</strong> These are "normal" recalls that get done in
                  regular workflow. They're not rushed, but they are not ignored either.
                </p>
              </div>
            </div>

            <div className='flex items-start gap-3 p-6 bg-muted/30 rounded-lg border-l-4 border-border'>
              <Badge className='bg-gray-600 text-white mt-1 flex-shrink-0'>Low Priority</Badge>
              <div className='flex-1'>
                <p className='font-semibold mb-1 text-sm'>Use For:</p>
                <ul className='text-sm text-muted-foreground space-y-1 list-disc list-inside leading-6'>
                  <li>Non-urgent cosmetic issues</li>
                  <li>Recalls with very flexible deadlines</li>
                  <li>Low-impact campaigns affecting few vehicles</li>
                  <li>Recalls that can be done "when convenient"</li>
                </ul>
                <p className='text-xs italic mt-2 text-muted-foreground leading-5'>
                  <strong>What this means:</strong> These are done when there's extra time or when
                  the customer is already at your facility for other service.
                </p>
              </div>
            </div>
          </div>

          <div className='bg-green-50 dark:bg-green-950/30 border-l-4 border-green-600 p-6 rounded-r-xl'>
            <div className='flex gap-4'>
              <CheckCircle className='h-6 w-6 text-green-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-base font-semibold text-green-900 dark:text-green-100 mb-2'>
                  Best Practice
                </h4>
                <p className='text-sm leading-6 text-green-800 dark:text-green-200'>
                  Don't make everything "High Priority"! If everything is urgent, nothing is urgent.
                  Reserve High Priority for truly critical issues. Most campaigns should be Medium
                  Priority.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Understanding Skill Levels */}
        <section className='mb-20' id='understanding-skill-levels'>
          <h2 className='text-3xl font-bold mb-6'>Understanding Skill Levels</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Skill level tells Vehix what kind of technician is qualified to complete this recall.
            This ensures complex work goes to experienced techs and simple work can be done by
            anyone.
          </p>

          <div className='overflow-x-auto mb-10'>
            <table className='w-full border-collapse'>
              <thead>
                <tr className='border-b-2 border-border'>
                  <th className='text-left p-4 text-sm font-semibold' />
                  <th className='text-center p-4 text-sm font-semibold'>High Skill</th>
                  <th className='text-center p-4 text-sm font-semibold'>Medium Skill</th>
                  <th className='text-center p-4 text-sm font-semibold'>Low Skill</th>
                </tr>
              </thead>
              <tbody className='text-sm'>
                <tr className='border-b border-border'>
                  <td className='p-4 font-medium'>Can do complex diagnostics</td>
                  <td className='text-center p-4'>
                    <CheckCircle className='h-5 w-5 text-green-600 mx-auto' />
                  </td>
                  <td className='text-center p-4'>
                    <CheckCircle className='h-5 w-5 text-green-600 mx-auto' />
                  </td>
                  <td className='text-center p-4'>
                    <span className='text-muted-foreground'>—</span>
                  </td>
                </tr>
                <tr className='border-b border-border'>
                  <td className='p-4 font-medium'>Can program ECUs</td>
                  <td className='text-center p-4'>
                    <CheckCircle className='h-5 w-5 text-green-600 mx-auto' />
                  </td>
                  <td className='text-center p-4'>
                    <span className='text-muted-foreground'>—</span>
                  </td>
                  <td className='text-center p-4'>
                    <span className='text-muted-foreground'>—</span>
                  </td>
                </tr>
                <tr className='border-b border-border'>
                  <td className='p-4 font-medium'>Can work independently</td>
                  <td className='text-center p-4'>
                    <CheckCircle className='h-5 w-5 text-green-600 mx-auto' />
                  </td>
                  <td className='text-center p-4'>
                    <CheckCircle className='h-5 w-5 text-green-600 mx-auto' />
                  </td>
                  <td className='text-center p-4'>
                    <span className='text-muted-foreground'>—</span>
                  </td>
                </tr>
                <tr className='border-b border-border'>
                  <td className='p-4 font-medium'>Can supervise others</td>
                  <td className='text-center p-4'>
                    <CheckCircle className='h-5 w-5 text-green-600 mx-auto' />
                  </td>
                  <td className='text-center p-4'>
                    <span className='text-muted-foreground'>—</span>
                  </td>
                  <td className='text-center p-4'>
                    <span className='text-muted-foreground'>—</span>
                  </td>
                </tr>
                <tr>
                  <td className='p-4 font-medium'>Typical experience level</td>
                  <td className='text-center p-4'>5+ years</td>
                  <td className='text-center p-4'>2-5 years</td>
                  <td className='text-center p-4'>0-2 years</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className='bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-600 p-6 rounded-r-xl'>
            <div className='flex gap-4'>
              <Info className='h-6 w-6 text-blue-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-base font-semibold text-blue-900 dark:text-blue-100 mb-2'>
                  How to Decide Skill Level
                </h4>
                <p className='text-sm leading-6 text-blue-800 dark:text-blue-200 mb-3'>
                  Think about the recall work itself:
                </p>
                <ul className='space-y-2 text-sm leading-6 text-blue-800 dark:text-blue-200'>
                  <li>
                    <strong>High Skill:</strong> Requires diagn diagnostics, programming, or
                    disassembly of major components
                  </li>
                  <li>
                    <strong>Medium Skill:</strong> Standard repair procedures, normal training
                    required
                  </li>
                  <li>
                    <strong>Low Skill:</strong> Simple part replacement, basic inspections, or
                    software updates
                  </li>
                </ul>
                <p className='mt-3 text-sm leading-6 text-blue-800 dark:text-blue-200'>
                  <strong>When in doubt, ask:</strong> "Could our newest technician do this safely
                  and correctly?" If no, it is probably Medium or High skill.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mobile Eligibility */}
        <section className='mb-20' id='mobile-eligibility-can-it-be-done-at-customer-location'>
          <h2 className='text-3xl font-bold mb-6'>
            Mobile Eligibility: Can It Be Done at Customer Location?
          </h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Mobile eligibility determines whether a recall can be completed at the customer's home
            or workplace (mobile service) or requires the vehicle to come to your facility.
          </p>

          <div className='grid gap-6 sm:grid-cols-2 mb-10'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-semibold'>Mobile Eligible (Yes)</CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div>
                  <p className='text-sm font-semibold mb-2'>Examples:</p>
                  <ul className='list-disc list-inside space-y-1 text-sm leading-6 text-muted-foreground'>
                    <li>Software updates or ECU reprogramming</li>
                    <li>Simple part replacements (clips, covers, labels)</li>
                    <li>Visual inspections</li>
                    <li>Work that can be done with tools in a mobile van</li>
                  </ul>
                </div>
                <p className='text-sm text-muted-foreground italic leading-6'>
                  Benefit: Customers love mobile service because they don't have to drop off their
                  car!
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-lg font-semibold'>Facility Required (No)</CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div>
                  <p className='text-sm font-semibold mb-2'>Examples:</p>
                  <ul className='list-disc list-inside space-y-1 text-sm leading-6 text-muted-foreground'>
                    <li>Work requiring a lift (suspension, exhaust, undercarriage)</li>
                    <li>Complex diagnostics needing dealer equipment</li>
                    <li>Major component replacement</li>
                    <li>Work requiring multiple technicians</li>
                  </ul>
                </div>
                <p className='text-sm text-muted-foreground italic leading-6'>
                  Reality: Some work just can't be done in a driveway.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className='bg-muted/30 border-l-4 border-border p-6 rounded-r-xl'>
            <div className='flex gap-4'>
              <FileText className='h-6 w-6 text-muted-foreground flex-shrink-0 mt-1' />
              <div>
                <p className='text-sm leading-6'>
                  <strong>Check with your service team:</strong> Some dealerships don't offer mobile
                  service at all. If that's your situation, you can mark everything as "Facility
                  Required" and not worry about mobile eligibility.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How to Configure a Campaign */}
        <section className='mb-20' id='how-to-configure-a-campaign'>
          <h2 className='text-3xl font-bold mb-6'>How to Configure a Campaign</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Let's walk through configuring a campaign from start to finish:
          </p>

          <div className='space-y-6 mb-10'>
            <div className='relative flex gap-4'>
              <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                1
              </div>
              <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
              <div className='flex-1 pb-2'>
                <h4 className='text-lg font-semibold text-foreground mb-2'>Find the Campaign</h4>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Use the search box to find the campaign by number or description. For example,
                  search "24V123" or "airbag".
                </p>
              </div>
            </div>

            <div className='relative flex gap-4'>
              <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                2
              </div>
              <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
              <div className='flex-1 pb-2'>
                <h4 className='text-lg font-semibold text-foreground mb-2'>Click to Edit</h4>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Click on the campaign row. This will expand it or open an edit dialog showing all
                  configuration options.
                </p>
              </div>
            </div>

            <div className='relative flex gap-4'>
              <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                3
              </div>
              <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
              <div className='flex-1 pb-2'>
                <h4 className='text-lg font-semibold text-foreground mb-2'>Set Priority Level</h4>
                <p className='text-sm leading-6 text-muted-foreground mb-2'>
                  Choose High, Medium, or Low based on safety criticality and deadlines. Remember:
                  most campaigns should be Medium!
                </p>
                <p className='text-sm leading-6 text-muted-foreground italic'>
                  Think: Is this a safety issue? Is there a deadline? Are customers complaining? If
                  yes to multiple, it is probably High Priority.
                </p>
              </div>
            </div>

            <div className='relative flex gap-4'>
              <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                4
              </div>
              <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
              <div className='flex-1 pb-2'>
                <h4 className='text-lg font-semibold text-foreground mb-2'>Assign Skill Level</h4>
                <p className='text-sm leading-6 text-muted-foreground mb-2'>
                  Select High, Medium, or Low based on the repair complexity and technician
                  qualifications needed.
                </p>
                <p className='text-sm leading-6 text-muted-foreground italic'>
                  Review the manufacturer repair instructions if you are not sure. Complex = High,
                  Standard = Medium, Simple = Low.
                </p>
              </div>
            </div>

            <div className='relative flex gap-4'>
              <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                5
              </div>
              <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
              <div className='flex-1 pb-2'>
                <h4 className='text-lg font-semibold text-foreground mb-2'>
                  Configure Mobile Eligibility
                </h4>
                <p className='text-sm leading-6 text-muted-foreground mb-2'>
                  Toggle Mobile Eligible on if the work can be done at customer location, off if it
                  requires facility equipment.
                </p>
                <p className='text-sm leading-6 text-muted-foreground italic'>
                  When in doubt, set to "No" (facility required). It is better to be conservative
                  and provide good service than promise mobile service and not deliver.
                </p>
              </div>
            </div>

            <div className='relative flex gap-4'>
              <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                6
              </div>
              <div className='flex-1 pb-2'>
                <h4 className='text-lg font-semibold text-foreground mb-2'>Save Changes</h4>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Click Save or Done. Your settings take effect immediately and will be used for
                  future routing and assignment.
                </p>
              </div>
            </div>
          </div>

          <div className='bg-red-50 dark:bg-red-950/30 border-l-4 border-red-600 p-6 rounded-r-xl'>
            <div className='flex gap-4'>
              <AlertTriangle className='h-6 w-6 text-red-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-base font-semibold text-red-900 dark:text-red-100 mb-2'>
                  Important: Test Your Settings
                </h4>
                <p className='text-sm leading-6 text-red-800 dark:text-red-200'>
                  After configuring a campaign, create a test route with a recall from that campaign
                  to verify your settings work as expected. Make sure technicians with the right
                  skill level are being suggested and the mobile eligibility is correct.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Common Campaign Workflows */}
        <section className='mb-20' id='common-campaign-workflows'>
          <h2 className='text-3xl font-bold mb-6'>Common Campaign Workflows</h2>

          <div className='space-y-10'>
            {/* Scenario 1 */}
            <div>
              <h3 className='text-lg font-semibold mb-4'>
                Scenario 1: New Safety Campaign Announced
              </h3>
              <p className='text-sm text-muted-foreground mb-6 leading-6'>
                Your manufacturer announces a new airbag recall affecting 50 vehicles in your area.
                Here's what to do:
              </p>
              <ol className='space-y-3 text-sm leading-6 list-decimal list-inside'>
                <li>Open Campaign Management and find the new campaign in the list</li>
                <li>
                  Read the manufacturer's campaign description to understand the safety issue and
                  repair requirements
                </li>
                <li>
                  Set Priority to <strong>High</strong> (it is a safety recall involving airbags)
                </li>
                <li>
                  Set Skill Level to <strong>High</strong> (airbag work requires certified
                  technicians)
                </li>
                <li>
                  Set Mobile Eligible to <strong>No</strong> (airbag replacement requires facility
                  equipment)
                </li>
                <li>Save your configuration</li>
                <li>
                  Notify your service team that this is a high-priority campaign requiring
                  experienced technicians
                </li>
                <li>Begin routing affected recalls to qualified technicians immediately</li>
              </ol>
            </div>

            {/* Scenario 2 */}
            <div>
              <h3 className='text-lg font-semibold mb-4'>Scenario 2: Software Update Campaign</h3>
              <p className='text-sm text-muted-foreground mb-6 leading-6'>
                A campaign for a simple software update affects 200 vehicles. It is not urgent but
                should be done eventually:
              </p>
              <ol className='space-y-3 text-sm leading-6 list-decimal list-inside'>
                <li>Find the campaign in Campaign Management</li>
                <li>
                  Set Priority to <strong>Medium</strong> (it should be done but isn't urgent)
                </li>
                <li>
                  Set Skill Level to <strong>Low</strong> (any technician can connect a laptop and
                  run the update)
                </li>
                <li>
                  Set Mobile Eligible to <strong>Yes</strong> (software updates can be done anywhere
                  with a laptop)
                </li>
                <li>Save configuration</li>
                <li>
                  These recalls will be included in normal routing and can be done as mobile service
                </li>
              </ol>
            </div>

            {/* Scenario 3 */}
            <div>
              <h3 className='text-lg font-semibold mb-4'>
                Scenario 3: Campaign Blitz (Clear Out Old Recalls)
              </h3>
              <p className='text-sm text-muted-foreground mb-6 leading-6'>
                You notice a campaign from 6 months ago still has 30 open recalls. Time for a
                focused push:
              </p>
              <ol className='space-y-3 text-sm leading-6 list-decimal list-inside'>
                <li>Find the aging campaign</li>
                <li>
                  Temporarily increase Priority to <strong>High</strong> to make it the focus
                </li>
                <li>Go to the Recall List and filter by this campaign number</li>
                <li>Group recalls by geographic area</li>
                <li>Create several routes assigning all 30 recalls to available technicians</li>
                <li>Track completion daily in Route Tracker</li>
                <li>
                  After all recalls are complete, you can change Priority back to Medium if needed
                </li>
              </ol>
            </div>
          </div>

          <div className='mt-10 bg-green-50 dark:bg-green-950/30 border-l-4 border-green-600 p-6 rounded-r-xl'>
            <div className='flex gap-4'>
              <CheckCircle className='h-6 w-6 text-green-600 flex-shrink-0 mt-1' />
              <div>
                <p className='text-sm leading-6 text-green-800 dark:text-green-200'>
                  <strong>Pro Tip:</strong> Campaign blitzes work best when you communicate clearly
                  with your team. Send an email or hold a brief team meeting explaining that "we are
                  focusing on Campaign XYZ this week" so everyone knows what's happening.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Common Questions */}
        <section className='mb-20' id='common-questions'>
          <h2 className='text-3xl font-bold mb-6'>Common Questions</h2>

          <div className='space-y-8'>
            <div>
              <h3 className='text-lg font-semibold mb-3'>
                Who decides campaign priority - us or the manufacturer?
              </h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                The manufacturer provides guidance (they might say "stop-sale" or "customer
                satisfaction"), but <strong>you</strong> make the final decision in Vehix. You know
                your local situation better than anyone. If customers are calling constantly about a
                campaign, you might bump its priority even if the manufacturer says it is routine.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-3'>
                Can I change campaign settings after recalls are already assigned?
              </h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                Yes! Changes to campaign settings apply immediately but don't retroactively change
                existing route assignments. If you change a campaign from Low Skill to High Skill,
                existing routes stay assigned to their current technicians, but <strong>new</strong>{' '}
                routes will suggest only high-skill technicians.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-3'>
                What if a campaign has both simple and complex repairs?
              </h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                Set the skill level based on the <strong>most complex</strong> part of the repair.
                It is better to have a highly skilled technician do simple work than to assign
                complex work to someone not qualified. The skilled tech will just finish faster on
                the easy ones.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-3'>
                How often should I review campaign settings?
              </h3>
              <p className='text-sm leading-6 text-muted-foreground mb-4'>
                Review campaign priorities monthly or whenever:
              </p>
              <ul className='space-y-2 text-sm leading-6 text-muted-foreground list-disc list-inside'>
                <li>A new campaign is announced</li>
                <li>Manufacturer updates campaign guidance</li>
                <li>Customer complaints increase about a specific issue</li>
                <li>A campaign is nearing a deadline</li>
                <li>Completion rates are lower than expected</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Next Steps */}
        <section className='mb-20' id='next-steps'>
          <h2 className='text-3xl font-bold mb-6'>Next Steps</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Now that you understand Campaign Management, learn about:
          </p>

          <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
            <Link to='/docs/features/technicians' className='block group'>
              <Card className='rounded-xl border shadow-sm h-full transition-all hover:border-primary/50'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold group-hover:text-primary transition-colors'>
                    Technician Management
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    Configure your technicians' skill levels to match with campaigns
                  </p>
                </CardContent>
              </Card>
            </Link>

            <Link to='/docs/features/manual-routing' className='block group'>
              <Card className='rounded-xl border shadow-sm h-full transition-all hover:border-primary/50'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold group-hover:text-primary transition-colors'>
                    Manual Routing
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    Create routes using your campaign priority settings
                  </p>
                </CardContent>
              </Card>
            </Link>

            <Link to='/docs/features/recall-list' className='block group'>
              <Card className='rounded-xl border shadow-sm h-full transition-all hover:border-primary/50'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-lg font-semibold group-hover:text-primary transition-colors'>
                    Recall List
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    Filter recalls by campaign to see what needs attention
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </section>

        {/* Final Help */}
        <div className='bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-600 p-6 rounded-r-xl'>
          <div className='flex gap-4'>
            <Info className='h-6 w-6 text-blue-600 flex-shrink-0 mt-1' />
            <div>
              <p className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                <strong>Need Help?</strong> Campaign configuration is one of the most important
                parts of Vehix. If you are unsure about how to configure a campaign, reach out to{' '}
                <a href='mailto:contact@kebi.ai' className='underline'>
                  contact@kebi.ai
                </a>{' '}
                - we are happy to help you make the right choices!
              </p>
            </div>
          </div>
        </div>
      </div>
    </DocsLayout>
  );
};
